import {
  InternationalAvailablePaymentType,
  InternationalAvailablePaymentTypesResult,
  InternationalPaymentSettings,
} from 'services/payment-intl';
import { isInIframe } from 'utils/iframe';
import { requestSdkExternalRedirect } from 'utils/sdkClose';

import {
  INTERNATIONAL_PAYMENT_RETURN_STORAGE,
  PAYMENT_METHOD,
  PAYMENT_METHOD_LABEL,
  PAYMENT_PROCESSOR,
  PAYMENT_TYPE_ID,
  STRIPE_RETURN_OPERATION,
  TRANSFERMATE_PAYMENT_RETURN_STORAGE,
} from './constants';
import {
  PaymentMethodKey,
  PaymentMethodOption,
  StripePaymentReturnData,
  StripeReturnOperation,
} from './types';

export function redirectToStripeCheckout(checkoutSessionUrl: string): void {
  if (isInIframe()) {
    requestSdkExternalRedirect(checkoutSessionUrl, 'stripe-payment');
    return;
  }

  window.location.replace(checkoutSessionUrl);
}

export function isInternationalPaymentEnabled(settings?: InternationalPaymentSettings): boolean {
  return settings?.is_international === true || settings?.isInternational === true;
}

export function isInternationalPaymentAvailable(settings?: InternationalPaymentSettings): boolean {
  return settings?.enableInternationalLeasingJourney === true;
}

export function getPaymentWebsiteHost(): string {
  if (typeof window === 'undefined') return '';
  return `${window.location.origin}${window.location.pathname}`;
}

export function getPaymentViewerPath(): string {
  if (typeof window === 'undefined') return '';

  return window.location.pathname;
}

export function getPaymentCurrentUrl(): string {
  if (typeof window === 'undefined') return '';

  return window.location.href;
}

export function persistTransferMatePaymentReturn(paymentKey: string): void {
  localStorage.setItem(TRANSFERMATE_PAYMENT_RETURN_STORAGE.FLAG, 'true');
  localStorage.setItem(TRANSFERMATE_PAYMENT_RETURN_STORAGE.PAYMENT_KEY, paymentKey);
}

export function readTransferMatePaymentReturn(): { paymentKey: string } | null {
  if (typeof window === 'undefined') return null;

  if (localStorage.getItem(TRANSFERMATE_PAYMENT_RETURN_STORAGE.FLAG) !== 'true') return null;

  return {
    paymentKey: localStorage.getItem(TRANSFERMATE_PAYMENT_RETURN_STORAGE.PAYMENT_KEY)?.trim() ?? '',
  };
}

export function clearTransferMatePaymentReturn(): void {
  if (typeof window === 'undefined') return;

  localStorage.removeItem(TRANSFERMATE_PAYMENT_RETURN_STORAGE.FLAG);
  localStorage.removeItem(TRANSFERMATE_PAYMENT_RETURN_STORAGE.PAYMENT_KEY);
}

export function persistStripePaymentReturn(data: StripePaymentReturnData): void {
  localStorage.setItem(INTERNATIONAL_PAYMENT_RETURN_STORAGE.FLAG, 'true');
  localStorage.setItem(INTERNATIONAL_PAYMENT_RETURN_STORAGE.PAYMENT_ID, data.paymentId);
  localStorage.setItem(
    INTERNATIONAL_PAYMENT_RETURN_STORAGE.STRIPE_PAYMENT_KEY,
    data.stripePaymentKey,
  );
  if (data.paymentMethod) {
    localStorage.setItem(INTERNATIONAL_PAYMENT_RETURN_STORAGE.PAYMENT_METHOD, data.paymentMethod);
  }
}

export function readStripePaymentReturn(): StripePaymentReturnData | null {
  if (typeof window === 'undefined') return null;

  const isStripeReturn = localStorage.getItem(INTERNATIONAL_PAYMENT_RETURN_STORAGE.FLAG) === 'true';
  if (!isStripeReturn) return null;

  const paymentMethod = localStorage.getItem(INTERNATIONAL_PAYMENT_RETURN_STORAGE.PAYMENT_METHOD);
  return {
    paymentId: localStorage.getItem(INTERNATIONAL_PAYMENT_RETURN_STORAGE.PAYMENT_ID)?.trim() ?? '',
    stripePaymentKey:
      localStorage.getItem(INTERNATIONAL_PAYMENT_RETURN_STORAGE.STRIPE_PAYMENT_KEY)?.trim() ?? '',
    ...(paymentMethod ? { paymentMethod: paymentMethod as PaymentMethodKey } : {}),
  };
}

export function clearStripePaymentReturn(): void {
  if (typeof window === 'undefined') return;

  localStorage.removeItem(INTERNATIONAL_PAYMENT_RETURN_STORAGE.FLAG);
  localStorage.removeItem(INTERNATIONAL_PAYMENT_RETURN_STORAGE.PAYMENT_ID);
  localStorage.removeItem(INTERNATIONAL_PAYMENT_RETURN_STORAGE.STRIPE_PAYMENT_KEY);
  localStorage.removeItem(INTERNATIONAL_PAYMENT_RETURN_STORAGE.PAYMENT_METHOD);
}

export function getStripeReturnOperation(): StripeReturnOperation | null {
  if (typeof window === 'undefined') return null;

  const operation = new URL(window.location.href).searchParams.get('op')?.toLowerCase();
  if (
    operation === STRIPE_RETURN_OPERATION.SUCCESS ||
    operation === STRIPE_RETURN_OPERATION.CANCEL
  ) {
    return operation;
  }

  return null;
}

export function clearStripeReturnOperation(): void {
  if (typeof window === 'undefined' || !getStripeReturnOperation()) return;

  const url = new URL(window.location.href);
  url.searchParams.delete('op');
  window.history.replaceState(window.history.state, document.title, url.toString());
}

function findPaymentType(
  available: readonly InternationalAvailablePaymentType[],
  paymentTypeId: number,
): InternationalAvailablePaymentType | undefined {
  return available.find((type) => Number(type.id) === paymentTypeId);
}

function isTransferMateCardEnabled(settings?: InternationalPaymentSettings): boolean {
  const transfermateSettings = settings?.transfermate ?? settings;
  return (
    transfermateSettings?.enable === true &&
    (transfermateSettings.is_card_enable === true || transfermateSettings.isCardEnable === true)
  );
}

export function getPaymentMethods(
  response?: InternationalAvailablePaymentTypesResult,
  settings?: InternationalPaymentSettings,
): PaymentMethodOption[] {
  const available =
    response && typeof response === 'object' && 'available' in response
      ? response.available
      : (response ?? []);
  const methods: PaymentMethodOption[] = [];

  const cardPaymentType = findPaymentType(available, PAYMENT_TYPE_ID.CARD);
  if (cardPaymentType) {
    methods.push({
      key: PAYMENT_METHOD.CARD,
      label: PAYMENT_METHOD_LABEL.CARD,
      paymentTypeId: PAYMENT_TYPE_ID.CARD,
      processor: isTransferMateCardEnabled(settings)
        ? PAYMENT_PROCESSOR.TRANSFERMATE
        : PAYMENT_PROCESSOR.STRIPE,
    });
  }

  const bacsPaymentType = findPaymentType(available, PAYMENT_TYPE_ID.BACS_DIRECT_DEBIT);
  // TransferMate Card replaces Electronic Bank Transfer (id 25), but BACS
  // remains an independent payment method when it is available.
  if (bacsPaymentType) {
    const chargesDueAutoPaymentEnabled =
      bacsPaymentType.chargesDueAutoPaymentEnable ??
      bacsPaymentType.charges_due_auto_payment_enable;
    methods.push({
      key: PAYMENT_METHOD.BACS_DIRECT_DEBIT,
      processor: PAYMENT_PROCESSOR.STRIPE,
      label: PAYMENT_METHOD_LABEL.BACS_DIRECT_DEBIT,
      paymentTypeId: PAYMENT_TYPE_ID.BACS_DIRECT_DEBIT,
      ...(chargesDueAutoPaymentEnabled !== undefined && {
        chargesDueAutoPaymentEnabled,
      }),
    });
  }

  const payByBankPaymentType = findPaymentType(available, PAYMENT_TYPE_ID.PAY_BY_BANK);
  if (payByBankPaymentType) {
    methods.push({
      key: PAYMENT_METHOD.PAY_BY_BANK,
      label: PAYMENT_METHOD_LABEL.PAY_BY_BANK,
      paymentTypeId: PAYMENT_TYPE_ID.PAY_BY_BANK,
      processor: PAYMENT_PROCESSOR.STRIPE,
    });
  }

  return methods;
}
